/**
 * Runs `fn` off the current task, once the browser is idle (or after
 * `timeout` ms, whichever comes first). Returns a cancel function.
 *
 * Primary use: keeping the analytics pixel fan-out (`ga.event` → gtag +
 * Vercel Analytics + PostHog) out of tap handlers and out of effects that
 * React flushes synchronously on discrete events. One bare
 * `gtag('event', …)` call measured 30–56 ms of main-thread time on a
 * 4x-throttled mobile profile — enough to visibly delay the paint the tap
 * was supposed to produce (mobile drawer, login modal). Deferring to idle
 * lets the UI commit and paint first; the 1s timeout guarantees the event
 * still fires promptly on a busy main thread.
 *
 * Do NOT use this for events fired immediately before a full page unload —
 * client-side `router.push` keeps timers alive, but a hard navigation may
 * drop the callback.
 *
 * `setTimeout(0)` is the Safari fallback (no `requestIdleCallback`), same
 * pattern as the PostHog init deferral in `src/components/Scripts.tsx`.
 */
export const deferToIdle = (fn: () => void, timeout = 1000): (() => void) => {
  if (typeof window.requestIdleCallback === 'function') {
    const handle = window.requestIdleCallback(fn, { timeout })
    return () => window.cancelIdleCallback(handle)
  }

  const handle = window.setTimeout(fn, 0)
  return () => window.clearTimeout(handle)
}
